import { psAxios, type RequestConfig } from '@patsnap/common-ps-http';
import type { RouteLocationNormalizedGeneric, Router } from 'vue-router';
import type { SharePageInterceptorService } from '../share-page-interceptor-service';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { RouterService } from '@telecom/shared/services/router';
import { isUserLogin } from '@telecom/shared/startup/user-auth';

/**
 * 分享页面拦截器服务
 * 该服务用于在分享页面中拦截请求，并替换请求的 baseURL
 * 以便于在分享页面中使用公共的 API
 * 该服务在路由变化时注册和注销拦截器
 */
@injectable()
export class SharePageInterceptorServiceImpl implements SharePageInterceptorService {
    private interceptorsNumber: number | undefined = undefined;
    private blackRouterList: { method: 'GET' | 'POST', url: string }[] = [
        // 黑名单路由,默认填写了一个为了写单元测试
        { method: 'GET', url: '/blacklist' },
    ];
    constructor(
        @inject(RouterService)
        private readonly _routerService: RouterService,
    ) { }

    public async registerAxiosInterceptor(): Promise<void> {
        this._routerService.beforeEach(async (to, from, next) => {
            const isLogin = await isUserLogin();
            // 分享页面或者用户未登录则需要请求公开的接口
            if (this._isMatchSharePage(to) || !isLogin) {
                this.interceptorsNumber = psAxios.interceptors.request.use(this.interceptor.bind(this));
            } else if (this.interceptorsNumber) {
                psAxios.interceptors.request.eject(this.interceptorsNumber);
                this.interceptorsNumber = undefined;
            }
            next();
        });
    }

    private interceptorHandel(config: RequestConfig): RequestConfig {
        config.baseURL = config.baseURL?.replace('/core-telecom-api/', '/public-share/');
        // 外观设计服务是 /fto-image-api/ 地址
        config.baseURL = config.baseURL?.replace('/fto-image-api/', '/public-share-image/');
        return config;
    }

    private interceptor(config: RequestConfig): RequestConfig {
        if (this._isMatchBlackRouter(config)) {
            return config;
        }
        return this.interceptorHandel(config);
    }


    /**
     * 判断是否是分享页面
     * @param to 路由对象
     * @returns 是否是分享页面
     */
    private _isMatchSharePage(to: RouteLocationNormalizedGeneric): boolean {
        return !!to.query.shareId;
    }

    /**
     * 判断是否是黑名单路由
     * @param config 请求配置
     * @returns 是否是黑名单路由
     */
    private _isMatchBlackRouter(config: RequestConfig): boolean {
        return !!this.blackRouterList?.find(path => {
            const methodMatch = config.method?.toLocaleLowerCase() === path.method.toLocaleLowerCase();
            const urlMatch = config.url && this._isExactUrlMatch(config.url, path.url);
            return methodMatch && urlMatch;
        });
    }

    /**
    * 精确匹配URL路径，确保不会匹配到子路由
    * @param requestUrl 请求URL
    * @param blacklistUrl 黑名单URL
    * @returns 是否匹配
    */
    private _isExactUrlMatch(requestUrl: string, blacklistUrl: string): boolean {
        // 移除开头和结尾的斜杠以标准化比较
        const normalizedRequestUrl = requestUrl.replace(/^\/|\/$/g, '');
        const normalizedBlacklistUrl = blacklistUrl.replace(/^\/|\/$/g, '');

        // 情况1: 完全相等
        if (normalizedRequestUrl === normalizedBlacklistUrl) {
            return true;
        }

        // 检查路径边界匹配
        const isPathBoundary = (index: number, str: string): boolean =>
            index < 0 || index >= str.length || str[index] === '/';

        // 以黑名单URL结尾，且边界正确
        if (normalizedRequestUrl.endsWith(normalizedBlacklistUrl)) {
            const boundaryIndex = normalizedRequestUrl.length - normalizedBlacklistUrl.length - 1;
            if (isPathBoundary(boundaryIndex, normalizedRequestUrl)) {
                return true;
            }
        }

        // 以黑名单URL开头，且边界正确
        if (normalizedRequestUrl.startsWith(normalizedBlacklistUrl)) {
            const boundaryIndex = normalizedBlacklistUrl.length;
            if (isPathBoundary(boundaryIndex, normalizedRequestUrl)) {
                return true;
            }
        }

        return false;
    }

}
